import { createContext, useState, useMemo } from "react";
import { alpha, createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
      grey: {
        900: "#fafafa",
        800: "#f4f4f6",
        700: "#cccccc",
        600: "#ababab",
        500: "#adadad",
        400: "#757575",
        300: "#757575",
        200: "#616161",
        100: "#1F2A40",
        70: "#101828",
        60: "#192238",

        50: "#fafafa",
        20: "rgba(255,255, 255, 0.12)", //for disabled
      },
      primary: {
        A100: "#F6FAFE",
        50: "#F6FAFE",
        900: "#BED9F4",
        850:"#144576",
        800: "#A8CCF0",
        700: "#A8CCF0",
        600: "#7CB2E9", // Smart CV primary teal
        500: "#5199E1",
        450: "#3B8CDE", // bg-primary - Smart CV teal
        400: "#257FDA",
        300: "#2173C4",
        200: "#1E66AE",
        150: "#1A5999",
        100: "#164C83",
        90: "#BED9F4",
        50: "#0F3357",
        20: "#ffffff", // text-primary-foreground
      },
      secondary: {
       
        20: "#ffe9e0",
        50: alpha("#ff5e24", 0.2),
        70: alpha("#ff6d38", 0.3),
        80: alpha("#ff6d38", 0.4),
        90: alpha("#ff6d38", 0.4),
        100: alpha("#ff6d38", 0.5),
        200: "#e63d00",
        300: "#f54100",
        400: "#FF4500",
        450: "#FF4500", //forsidebar

        500: "#ff5314",
        600: "#ff5e24",
        700: "#ff6d38",
        750:"#FF512F",

        800: "#ff8457",
        900: "#ff9e7a",
        A100: "#ffb89e",
        70: "#fff8ea",
        650: "#c54307",
      },
      // Smart CV status colors
      actives: {
        100: alpha("#22c55e", 0.1),
        200: "#86efac",
        300: "#4ade80",
        400: "#22c55e", // green for "Actives"
        500: "#16a34a",
        600: "#15803d",
      },
      brouillons: {
        100: alpha("#f59e0b", 0.1),
        200: "#fcd34d",
        300: "#fbbf24",
        400: "#f59e0b", // yellow for "Brouillons"
        500: "#d97706",
        600: "#b45309",
      },
      fermees: {
        100: alpha("#f87171", 0.1),
        200: "#fca5a5",
        300: "#f87171",
        400: "#f87171", // red/pink for "Fermées"
        500: "#ef4444",
        600: "#dc2626",
      },
      redAccent: {
        50: alpha("#fa8480", 0.1),

        100: alpha("#f74640", 0.1),
        150: "#672523", //for snackbar 
        200: "#f1b9b7",
        300: "#e99592",
        400: "#ff5c5c",
        500: "#db4f4a",
        600: "#e2504b",
        700: "#e9bfbe",
        800: "#58201e",
        900: "#2c100f",

        70: "#fededd",
        650: "#832f2c",
      },
      Pink: {
        100: alpha("#f472b6", 0.1),
        400: "#f472b6",
        600: "#831843",
      },
      Orange: {
        100: alpha("#ff9933", 0.1),
        150: "#6b3600",
        200: "#ffb56b",
        400: "#ff9933",
        600: "#ff9429",
        700: "#ffdebd",
        800: "#ffcf9e",
        900: "#ffebd6",
        70: "#ffebd6",
        650: "#cc6600",
      },
      Yellow: {
        100: alpha("#f59e0b", 0.1),
        200: "#fcd34d",
        300: "#f59e0b", // warning/accent
        400: "#eab308",
        600: "#ca8a04",
      },
      green: {
        900: "#9cff6b",
        800: "#96ff61",
        700: "#9bf271",
        750: "#358c0b",

        600: "#8bf05a",
        500: "#6aeb2b",
        400: "#59e913",
        300: "#50d211",
        200: "#47ba0f",
        150: "#358c0b",
        100: alpha("#9bf271", 0.2),
        70: "#cdf8b8",

        50: alpha("#59e913", 0.1),

      },
      Blue: {
        100: alpha("#00bcd4", 0.2),
        200: "#4dd0e1",
        300: "#00bcd4",
        400: "#00a1b5", // bg-accent
        600: "#00838f",
        700: "#006064",
        800: "#004d57",
        900: "#004d57",
      },
      purple: {
        100: alpha("#a855f7", 0.1),
        200: "#c084fc",
        300: "#a855f7",
        400: "#9333ea",
        500: "#7e22ce",
        600: "#6b21a8",
        800: "#5b21b6",
        900: "#4c1d95",
      },
      indigo: {
        100: alpha("#6366f1", 0.1),
        200: "#a5b4fc",
        300: "#818cf8",
        400: "#6366f1",
        500: "#4f46e5",
        600: "#4338ca",
        800: "#3730a3",
        900: "#312e81",
      },
      teal: {
        100: alpha("#00bcd4", 0.1),
        200: "#4dd0e1",
        300: "#00bcd4",
        400: "#00a1b5",
        500: "#00838f",
        600: "#006064",
        800: "#004d57",
        900: "#004d57",
      },
      brown: {
        100: alpha("#8b5cf6", 0.1),
        200: "#d1d5db",
        300: "#9ca3af",
        400: "#6b7280",
        500: "#475569",
        600: "#374151",
        800: "#1f2a44",
        900: "#1e293b",
      },
      skyBlue: {
        100: alpha("#00bcd4", 0.1),
        150: "#004d57",
        200: "#80deea",
        300: "#4dd0e1",
        400: "#00bcd4",
        500: "#00acc1",
        600: "#0097a7",
        700: "#0af7ff",
        800: "#33f8ff",
        900: "#57f9ff"
      },
    }
    : {
      grey: {
        20: "#fafafa", //for disabled
        50: "#fafafa",
        60: "#fafafa",

        70: "#ffffff",
        100: "#f4f4f6",
        200: "#eeeeee",
        300: "#e0e0e0",
        400: "#b3b3b3",
        500: "#8c8c8c",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#2b2b2b",
      },
      primary: {
        20: "#F6FAFE",
        50: "#F6FAFE",
        60: "#BED9F4",
        90: "#A8CCF0",
        100: "#A8CCF0",
        150: "#7CB2E9", // Smart CV primary teal
        200: "#5199E1",
        300: "#3B8CDE", // bg-primary - Smart CV teal
        400: "#257FDA",
        450: "#2173C4",
        500: "#1E66AE",
        600: "#1A5999",
        700: "#164C83",
        800: "#144576",
        850:"#144576",  

        900: "#0F3357",
        A100: "#ffffff", // text-primary-foreground
      },
      secondary: {

        20: "#946212",
        50: "#fff8ea",
        80: "#fef0d6",
        90: "#fee9c1",
        100: "#fde2ac",
        200: "#fddb98",
        300: "#fdd383",
        400: "#fcc559",
        450: "#fbb630", //forsidebar

        500: "#e2a42b",
        600: "#F09819",
        750:"#FF512F",
        700: "#de931b",
        800: "#ad7215",
        900: "#946212",
        A100: "#ea6e00",
        70: "#fff8ea",
        650: "#c54307",
      },
      // Smart CV status colors (from the interface)
      actives: {
        100: "#dcfce7", // light green background
        200: "#bbf7d0",
        300: "#86efac",
        400: "#22c55e", // green for "Actives" status
        500: "#16a34a",
        600: "#15803d",
      },
      brouillons: {
        100: "#fef3c7", // light yellow background
        200: "#fde68a",
        300: "#fcd34d",
        400: "#f59e0b", // yellow for "Brouillons" status
        500: "#d97706",
        600: "#b45309",
      },
      fermees: {
        100: "#fee2e2", // light red/pink background
        200: "#fecaca",
        300: "#fca5a5",
        400: "#f87171", // red/pink for "Fermées" status
        500: "#ef4444",
        600: "#dc2626",
      },
      BlueCarry: {
        50: "#e0f2fe",
        100: "#bae6fd",
        150: "#bae6fd",
        200: "#7dd3fc",
        300: "#38bdf8",
        400: "#0ea5e9",
        500: "#00a1b5",
        600: "#00838f",
        800: "#006064",
        900: "#004d57",
        A100: "#082f49",
      },
      Blue: {
        100: alpha("#00bcd4", 0.2),
        200: "#4dd0e1",
        300: "#00bcd4",
        400: "#00a1b5", // bg-accent
        600: "#00838f",
        700: "#006064",
        800: "#004d57",
        900: "#004d57",
      },
      Pink: {
        100: "#fce7f3",
        400: "#ec4899",
        600: "#be185d",
      },
      Orange: {

        100: "#ffebd6",
        150: "#ffe0c2",
        200: "#ffb56b",

        400: "#ff850a",
        600: "#cc6600",
        700: "#b35900",
        800: "#853900",
        900: "#944200",
        70: "#ffebd6",
        650: "#cc6600",
      },
      Yellow: {
        100: "#fef3c7",
        150: "#fef08a",
        200: "#fde68a",
        300: "#f59e0b", // warning/accent
        400: "#eab308",
        600: "#ca8a04",
        800: "#a16207",
        900: "#854d0e",
      },
      green: {
        50: "#f1feec",
        70: "#cdf8b8",
        100: "#cdf8b8",
        150: "#9bf271",
        200: "#8bf05a",
        300: "#6aeb2b",
        400: "#59e913",
        500: "#50d211",
        600: "#47ba0f",
        700: "#358c0b",
        750: "#358c0b",
        800: "#2d750a",
        900: "#1b4606",

      },
      redAccent: {
        // 100: alpha("#f74640", 0.1),
        50: '#fff5f5',

        100: '#fededd',
        150: '#fededd', //for snackbar 
        200: "#f1b9b7",
        300: "#e99592",
        400: "#eb110a",
        500: "#db4f4a",
        600: "#cf2c26",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",

        70: "#fededd",
        650: "#832f2c",
      },
      purple: {
        100: "#f3e8ff",
        200: "#e9d5ff",
        300: "#d8b4fe",
        400: "#a855f7",
        500: "#9333ea",
        600: "#7e22ce",
        800: "#6b21a8",
        900: "#5b21b6",
      },
      indigo: {
        100: "#e0e7ff",
        200: "#c7d2fe",
        300: "#a5b4fc",
        400: "#6366f1",
        500: "#4f46e5",
        600: "#4338ca",
        800: "#3730a3",
        900: "#312e81",
      },
      teal: {
        100: "#e0f7fa",
        200: "#4dd0e1",
        300: "#00bcd4",
        400: "#00a1b5",
        500: "#00838f",
        600: "#006064",
        800: "#004d57",
        900: "#004d57",
      },
      brown: {
        100: "#f5f5f4",
        200: "#e7e5e4",
        300: "#d6d3d1",
        400: "#78716c",
        500: "#57534e",
        600: "#44403c",
        800: "#292524",
        900: "#1c1917",
      },
      skyBlue: {
        100: "#e0f7fa",
        150: "#ccfaff",
        200: "#b2ebf2",
        300: "#80deea",
        400: "#00bcd4",
        500: "#00acc1",
        600: "#0097a7",
        700: "#00818f",
        800: "#00838f",
        900: "#006064"
      },
    }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
          // palette values for dark mode
          primary: {
            main: colors.primary[400],
          },
          secondary: {
            main: colors.secondary[400],
          },

          background: {
            navbar: '#0A0E1A',
            default: '#0A0E1A', // deep blue-black background
            paper: '#0f1424',
            card: '#0A0E1A',
            secondCard: '#111827', // lighter color for nested cards
          
          },
          //the default text color  
          text: {
            primary: '#FFFFFF',
          },
        }
        : {
          // palette values for light mode
          primary: {
            main: colors.primary[400],
          },
          secondary: {
            main: colors.secondary[400],
          },
          // neutral: {
          //   dark: colors.grey[700],
          //   main: colors.grey[500],
          //   light: colors.grey[100],
          // },
          background: {
            default: "#F9FAFB",
            paper: "#ffffff",
            card: "#ffffff", // ensure cards are light in light mode
            navbar: "#ffffff",
            subcard:"#ffffff",
            divider:colors.grey[300],
          },
          //the default text color  
          text: {
            primary: colors.grey[800],
          },
        }),
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#0A0E1A' : '#ffffff',
            backgroundImage: 'none', // This removes the overlay effect
          },
        },

      },
    },
    typography: {
      fontFamily: "Nunito Sans",
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => { },
});

export const useMode = () => {
  const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'light');

  const colorMode = useMemo(() => ({
    toggleColorMode: () => {
      setMode((prev) => {
        const newMode = prev === 'light' ? 'dark' : 'light';
        localStorage.setItem('themeMode', newMode);
        return newMode;
      });
    }
  }), [])

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
